/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';

import Auth0 from 'auth0-js';

import Loader from '../theme/Loader';
import PasswordReset from '../../services/password-reset';
import sentry from '../../services/sentry';
import Env from '../../services/environment';

export const Auth0Context = React.createContext();

export const useAuth0 = () => useContext(Auth0Context);

const { captureException } = sentry();

const Auth0Provider = ({ children, auth0Config: auth0ConfigProps, auth0RPConfig }) => {
  const [auth0Client, setAuth0Client] = useState();
  const [isLoading, setIsLoading] = useState();
  const [databaseConnection, setDatabaseConnection] = useState('NE-Digital-ID-Platform-Connection');

  useEffect(() => {
    const initAuth0 = () => {
      const config = auth0ConfigProps;
      const params = {
        /* additional configuration needed for use of custom domains */
        overrides: {
          __tenant: config.auth0Tenant,
          __token_issuer: config.authorizationServer.issuer,
        },
        domain: config.auth0Domain,
        clientID: config.clientID,
        redirectUri: config.callbackURL,
        responseType: 'code',
        ...config.internalOptions,
      };

      setIsLoading(true);

      const Auth0Client = new Auth0.WebAuth(params);

      setAuth0Client(Auth0Client);

      setDatabaseConnection(Env.realm(config.clientID));

      setIsLoading(false);
    };

    if (typeof auth0ConfigProps !== 'undefined') {
      // auth0Config will be undefined on the initial load (pre-rendered)
      // we make use of React.useEffect hook to listen to the change of auth0Config
      // and therefore initialize initAuth0 on subsequent client-side rerender
      initAuth0();
    }
  }, [auth0ConfigProps]);

  const signUp = ({ email, mobile, password, marketingConsentPreference }, signUpCallback) => {
    setIsLoading(true);
    auth0Client.redirect.signupAndLogin(
      {
        connection: databaseConnection,
        email,
        password,
        user_metadata: {
          country_code: '65',
          phone_number: `${mobile}`,
          marketing_consent_preference: (
            typeof marketingConsentPreference !== 'undefined'
          ).toString(),
        },
      },
      (error, result) => {
        if (error) captureException(error.description ? new Error(error.description) : error);
        setIsLoading(false);
        if (signUpCallback) signUpCallback(error, result);
      }
    );
  };

  const login = ({ email, password }, loginCallback) => {
    setIsLoading(true);
    auth0Client.login(
      {
        realm: databaseConnection,
        email,
        password,
      },
      (error, result) => {
        setIsLoading(false);
        if (error) captureException(error.description ? new Error(error.description) : error);
        if (loginCallback) loginCallback(error, result);
      }
    );
  };

  const forgotPassword = ({ email }, resetCallback) => {
    setIsLoading(true);
    auth0Client.changePassword(
      {
        connection: databaseConnection,
        email,
      },
      (error, result) => {
        setIsLoading(false);
        if (error) captureException(error.description ? new Error(error.description) : error);
        if (resetCallback) resetCallback(error, result);
      }
    );
  };

  const resetPassword = (values, userCallback) => {
    setIsLoading(true);
    PasswordReset(values, (user, error) => {
      setIsLoading(false);
      // we are not sending errors to Sentry here
      // as it has been done at API level (catch block of POST request)
      if (userCallback) userCallback(user, error);
    });
  };

  return (
    <Auth0Context.Provider
      value={{
        isLoading,
        signUp,
        login,
        forgotPassword,
        resetPassword,
        auth0RPConfig,
      }}
    >
      <>
        {isLoading && <Loader />}
        {children}
      </>
    </Auth0Context.Provider>
  );
};

Auth0Provider.propTypes = {
  children: PropTypes.node.isRequired,
  auth0Config: PropTypes.object,
  auth0RPConfig: PropTypes.object,
};

Auth0Provider.defaultProps = {
  auth0Config: undefined,
  auth0RPConfig: undefined,
};

export default Auth0Provider;
